'use client';

import Image from 'next/image';
import Link from 'next/link';
import { z } from 'zod';

import CallToAction from '~/components/atoms/CallToAction';
import Heading from '~/components/atoms/Heading';
import { modelSiteOptions } from '~/lib/api/models';
import { useHydrationSafeResponsive } from '~/lib/hooks/useResponsive';

type PageNotFoundProps = NonNullable<
  z.infer<
    typeof modelSiteOptions.schema
  >['siteOptionsPage']['siteOptions']['notFound']
>;

const PageNotFound: React.FC<PageNotFoundProps> = ({
  primaryTitle,
  secondaryTitle,
  linkText,
  backgroundImage,
}) => {
  const isDesktop = useHydrationSafeResponsive('desktop');

  return (
    <header className="relative isolate flex grid-cols-[4fr_6fr] items-center justify-center lg:h-[calc(100svh-140px)]">
      <aside className="bg-brand-secondary relative z-10 w-full px-4 pt-20 pb-10 text-white lg:w-auto lg:p-24">
        <div className="flex flex-col items-center gap-y-6 lg:min-w-110 lg:gap-y-20">
          <div className="flex min-w-110 flex-col items-center gap-y-6 lg:gap-y-20">
            <div className="row-start-2 flex flex-col items-center gap-y-2 self-center text-center">
              {secondaryTitle && (
                <div className="text-brand-quinary relative text-xs">
                  {secondaryTitle}
                </div>
              )}

              {primaryTitle && (
                <Heading as="h1" level={3}>
                  {primaryTitle || '404'}
                </Heading>
              )}
            </div>

            <CallToAction as={Link} href="/">
              {linkText || 'Retour à l’accueil'}
            </CallToAction>
          </div>

          {!isDesktop && (
            <div className="relative w-full">
              {backgroundImage && (
                <Image
                  src={backgroundImage.node.sourceUrl}
                  alt={backgroundImage.node.altText}
                  placeholder={
                    backgroundImage.node.blurDataUrl ? 'blur' : 'empty'
                  }
                  blurDataURL={backgroundImage.node.blurDataUrl || undefined}
                  className="relative h-auto w-full"
                  width={460}
                  height={280}
                  priority
                  unoptimized
                />
              )}
            </div>
          )}
        </div>
      </aside>

      {isDesktop && (
        <div className="absolute top-0 left-0 flex h-full w-full bg-black">
          {backgroundImage && (
            <Image
              src={backgroundImage.node.sourceUrl}
              alt={backgroundImage.node.altText}
              placeholder={backgroundImage.node.blurDataUrl ? 'blur' : 'empty'}
              blurDataURL={backgroundImage.node.blurDataUrl || undefined}
              className="h-full w-full object-cover"
              priority
              fill
              unoptimized
            />
          )}
        </div>
      )}
    </header>
  );
};

export default PageNotFound;
